import { Link, createFileRoute } from '@tanstack/react-router'
import type { AdminOverview } from '@algoboo/schema/api'
import { Card, Pill, Skeleton, formatCount, fullTime, timeAgo } from '@algoboo/ui'
import { adminOverviewQuery, formatMicroUsd, useAdminOverview } from '../lib/admin'
import { useSession } from '../components/session'
import { AdminError, BarList, ColumnChart, InlineBar, Table, Td, Tile } from '../components/admin/parts'
import { prefetchSignedIn } from '../lib/route-data'

export const Route = createFileRoute('/admin/')({
  loader: ({ context: { queryClient: qc } }) => prefetchSignedIn(qc, (me) => [me.isAdmin !== false && qc.ensureQueryData(adminOverviewQuery())]),
  component: AdminOverviewPage,
})

function AdminOverviewPage() {
  const { me } = useSession()
  const q = useAdminOverview(!!me)
  if (q.isLoading) return <Skeleton className="h-96 rounded-xl" />
  if (q.error) return <AdminError error={q.error} refetch={() => void q.refetch()} />
  const o = q.data!
  return (
    <div className="space-y-6" data-testid="admin-overview">
      <Tiles o={o} />
      <div className="grid gap-4 lg:grid-cols-[3fr_2fr]">
        <Card className="p-5">
          <h2 className="mb-1 text-[15px] font-semibold">Model spend</h2>
          <ColumnChart
            testId="admin-spend-chart"
            title="Charged model spend per UTC day, last 7 days"
            data={o.modelSpendByDay.map((d) => ({ label: d.day, value: Number(d.chargedMicro) / 1e6, display: formatMicroUsd(d.chargedMicro) }))}
          />
        </Card>
        <Card className="p-5">
          <h2 className="mb-1 text-[15px] font-semibold">Agents</h2>
          <BarList
            testId="admin-agents-chart"
            title={`${formatCount(o.counts.agents)} agents by status`}
            data={(['active', 'paused', 'draft', 'retired'] as const).map((s) => ({ label: s, value: o.agentsByStatus[s], display: formatCount(o.agentsByStatus[s]) }))}
          />
        </Card>
      </div>
      <QueueByKind o={o} />
      <Indexers o={o} />
      <p className="font-mono text-[11px] text-ink-4">as of {fullTime(o.asOf)} · refreshes every 30 s</p>
    </div>
  )
}

function Tiles({ o }: { o: AdminOverview }) {
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4" data-testid="admin-tiles">
      <Tile label="Users" value={formatCount(o.counts.users)} />
      <Tile label="Agents active" value={`${formatCount(o.counts.activeAgents)} / ${formatCount(o.counts.agents)}`} hint={`${o.agentsByStatus.draft} drafts · ${o.agentsByStatus.paused} paused`} />
      <Tile label="Trading wallets" value={formatCount(o.counts.wallets)} hint={`${formatCount(o.counts.activePermissions)} active permissions`} />
      <Tile label="Tokens live" value={formatCount(o.counts.tokens)} />
      <Tile label="Spend today" value={formatMicroUsd(o.modelSpendTodayMicro)} hint="charged, UTC day" />
      <Tile
        label="Queue"
        value={`${formatCount(o.queue.queued)} · ${formatCount(o.queue.running)}`}
        hint={o.queue.oldestQueuedAt ? `queued · running, oldest ${timeAgo(o.queue.oldestQueuedAt)}` : 'queued · running'}
      />
      <Tile
        testId="admin-tile-dead"
        label="Dead · failed"
        value={`${formatCount(o.queue.dead)} · ${formatCount(o.queue.failed)}`}
        tone={o.queue.dead > 0 ? 'bad' : o.queue.failed > 0 ? 'warn' : undefined}
        hint={
          <Link to="/admin/jobs" search={{ status: 'dead' }} className="hover:text-ink hover:underline">
            Open dead jobs
          </Link>
        }
      />
    </div>
  )
}

function QueueByKind({ o }: { o: AdminOverview }) {
  const max = Math.max(0, ...o.queueByKind.map((k) => k.queued + k.running))
  return (
    <section aria-labelledby="admin-queue">
      <h2 id="admin-queue" className="mb-3 text-[15px] font-semibold">
        Queue by kind
      </h2>
      {o.queueByKind.length === 0 ? (
        <Card className="px-4 py-6 text-center text-[13px] text-ink-3">No queued, running, failed or dead jobs.</Card>
      ) : (
        <Table head={['Kind', 'Depth (queued + running)', 'Failed', 'Dead']} testId="admin-queue-table" caption="Queue depth and failures per job kind">
          {o.queueByKind.map((k) => (
            <tr key={k.kind}>
              <Td mono>{k.kind}</Td>
              <Td>
                <InlineBar value={k.queued + k.running} max={max} label={`${k.queued} queued, ${k.running} running`} />
              </Td>
              <Td mono>{k.failed > 0 ? <JobsLink kind={k.kind} status="failed" n={k.failed} /> : 0}</Td>
              <Td mono>{k.dead > 0 ? <JobsLink kind={k.kind} status="dead" n={k.dead} /> : 0}</Td>
            </tr>
          ))}
        </Table>
      )}
    </section>
  )
}

function JobsLink({ kind, status, n }: { kind: string; status: 'failed' | 'dead'; n: number }) {
  return (
    <Link to="/admin/jobs" search={{ status, kind: kind as never }} className="inline-flex items-center gap-1.5 hover:underline">
      <Pill tone={status === 'dead' ? 'bad' : 'warn'} icon="alert">
        {n} {status}
      </Pill>
    </Link>
  )
}

function Indexers({ o }: { o: AdminOverview }) {
  const rows = [...o.indexers.map((i) => ({ ...i, name: i.name })), ...(o.stream ? [{ name: 'live stream', ...o.stream }] : [])]
  return (
    <section aria-labelledby="admin-indexers">
      <h2 id="admin-indexers" className="mb-3 text-[15px] font-semibold">
        Indexers {o.headBlock !== null && <span className="font-mono text-[12px] font-normal text-ink-3">· head #{o.headBlock}</span>}
      </h2>
      <Table head={['Cursor', 'Lag', 'Updated']} testId="admin-indexers-table" caption="Indexer cursors behind the chain head">
        {rows.length === 0 && (
          <tr>
            <Td className="py-4 text-ink-3">No block cursors yet (the indexers write them after their first run).</Td>
            <Td />
            <Td />
          </tr>
        )}
        {rows.map((i) => (
          <tr key={i.name}>
            <Td mono>{i.name}</Td>
            <Td mono>
              {i.lagBlocks === null ? (
                <span className="text-ink-3">unknown</span>
              ) : i.lagBlocks > 50 ? (
                <Pill tone="warn" icon="alert">
                  {i.lagBlocks} blocks
                </Pill>
              ) : (
                `${i.lagBlocks} blocks`
              )}
            </Td>
            <Td mono className="text-ink-3">{i.updatedAt ? <time title={fullTime(i.updatedAt)}>{timeAgo(i.updatedAt)}</time> : '—'}</Td>
          </tr>
        ))}
      </Table>
    </section>
  )
}
